/**
 * The Publish control — the verb journey 2 was missing.
 *
 * WHAT THIS RENDERS, AND WHY IT IS NOT A SPINNER
 *   Publication is asynchronous. `app.publish_draft` freezes the candidate and
 *   writes a job row in one transaction (`0033:1413-1473`); the commit, the
 *   bundle upload and the move of `current_release_id` happen afterwards in a
 *   worker process. So at the moment the button is pressed nothing has been
 *   published, and a control that said "published" then would be lying.
 *
 *   There is a measured crash window on top of that: the worker pushes the commit
 *   at `packages/worker/src/worker.ts:307` and finalises at `:232`, and only
 *   `finalizePublication` moves `current_release_id`. In between, the commit is on
 *   the remote and the skill is still serving its previous release. That is PUB-03
 *   working — the old release stays usable through an injected failure — and the
 *   only honest rendering of it is "still publishing", never "published".
 *
 * THE FOUR STATES, AND WHY EACH IS DISTINGUISHABLE
 *   - NOT YET APPROVED. V1 §6 step 6 requires a human to approve an exact
 *     content hash before publication, and `app.publish_draft` recomputes that
 *     hash at the door (`0033:1386-1393`). So this is not a UI preference: a
 *     button enabled here would be refused by the database, and the reason is
 *     shown rather than the button merely greyed.
 *   - IN FLIGHT. `queued` or `running`, with the attempt count. Announced through
 *     a live region, because nothing else on the page changes.
 *   - SUCCEEDED. The skill's CURRENT release, named for what it is. A later
 *     publication can move it, so this is not claimed to be the release this job
 *     produced — `0048`'s header says the same and for the same reason.
 *   - FAILED. The worker's own `last_error`, verbatim, plus a retry.
 *
 * THE RETRY, AND WHY IT IS NOT THE SAME REQUEST
 *   `app.publish_draft` accepts a draft in state `publish_failed` and treats the
 *   retry as a NEW job for the SAME approval (`0033:1335-1339`) — the content did
 *   not change, so the approval still covers it, and the failed job keeps its own
 *   row for reconciliation. So retry is a fresh idempotency key against the same
 *   route. Re-using the old key would return the OLD job instead, which is the
 *   idempotent-replay path and would show a person the failure they are trying to
 *   leave behind.
 *
 * ACCESSIBILITY, AND WHAT WAS MEASURED RATHER THAN ASSUMED
 *   P3's gate names keyboard and screen-reader correctness. The rules this panel
 *   holds itself to, each of which a check can fail:
 *
 *   - A real `<button>`, so it is in the tab order and fires on Enter and Space
 *     without a keydown handler written here.
 *   - When the control cannot be used it is `disabled` AND the reason is visible
 *     TEXT in the panel, not a tooltip and not `title`. A disabled control is not
 *     focusable, so a keyboard user cannot read a reason that is only attached to
 *     it — which is why the reason is not attached only to it.
 *   - `aria-busy` on the panel while the worker holds the job, and a
 *     `role="status"` region that announces transitions. `role="alert"` for the
 *     failure, because it is the one state that must interrupt.
 *   - Colour is never alone: every status uses `tag()`, which renders a glyph
 *     (`aria-hidden`) plus the word, so nothing depends on the tone class.
 *   - The worker's `last_error` goes through `html` and is therefore escaped. It
 *     is a database string naming a path and a status code; it is not markup and
 *     must never become any.
 *   - The release id is shown in full, not truncated to a decorative short form:
 *     it is the thing a person needs in order to file a bug or check the database,
 *     and `shortHash` is for a digest somebody is comparing in their head.
 */

import type { AppShell } from '../app.js';
import type { Failure } from '../api/client.js';
import * as api from '../api/queries.js';
import type { DraftRow, PublishJobStatusRow } from '../api/queries.js';
import { type RawHtml, attr, html } from '../lib/html.js';
import { formatDateTime } from '../lib/format.js';
import { presentFailure, tag } from '../ui/components.js';

/** How long to wait between reads of the publication, in milliseconds. */
export const PUBLISH_POLL_MS = 1500;

/** A publication that has reached a state no further poll will change. */
export function isTerminal(state: string): boolean {
  return state === 'succeeded' || state === 'failed' || state === 'cancelled';
}

export interface PublishAvailability {
  /** Whether a press would be accepted, and if not, the sentence that says why. */
  readonly allowed: boolean;
  readonly reason: string;
}

/**
 * Whether Publish can be pressed right now, and — when it cannot — the one
 * sentence a person can act on.
 *
 * The order matters: a draft that has never been approved is refused by the
 * database for that reason, and a draft that is mid-publication is refused
 * because another publication already holds the skill's single active slot
 * (`drafts_one_active_publication_idx`, `0001:822`). Saying "publish it" for the
 * first and "try again" for the second would send somebody to the wrong place.
 */
/**
 * Whether the last publication failed, from EITHER row that records it.
 *
 * The two are not the same row and they are not updated by the same code: the
 * worker marks `publish_jobs.state` failed with its own `last_error`, and the
 * draft row moves to `publish_failed` when that job is given up on
 * (`0048:158` calls this out — "It can say 'failed'" while the draft row has
 * not moved yet). Keying on the draft alone therefore renders "being published
 * right now" over a publication that has already stopped, and keys the retry
 * control on a window rather than on the failure.
 */
export function publicationFailed(
  draft: DraftRow,
  status: PublishJobStatusRow | null,
): boolean {
  // `typeof`, NOT `!== null`. `ScreenState.data` is `unknown` and is cast at
  // the call site, so a fixture or an older payload that omits the key arrives
  // as `undefined` — which walks straight past an identity comparison and then
  // throws on `status.state`. `editor.ts` already made this correction for the
  // same reason on the same cast, and a second agent found this one by running
  // a suite rather than by reading the type.
  return (
    draft.state === 'publish_failed' ||
    (typeof status === 'object' && status !== null && status.state === 'failed')
  );
}

/**
 * A job row, or null when there is not one to read.
 *
 * ONCE, AT THE EDGE, so the rest of this file can compare against `null` and
 * mean it. `ScreenState.data` is `unknown` and every view casts it, so
 * `publication` arrives as `undefined` rather than as the `null` its type
 * promises whenever the key is absent — and `undefined !== null` is true, so
 * every `status !== null` guard in a renderer passes straight through to
 * `status.state` and throws. `@ResponsiveLayoutFixes` measured the consequence:
 * ten cases in the UI contract corpus dying on a line before they asserted
 * anything. A guard that has to be written at each dereference is a guard that
 * will be forgotten at the next one; this is the single place it is not.
 */
function asJobStatus(status: PublishJobStatusRow | null | undefined): PublishJobStatusRow | null {
  // `typeof` twice, not `as`. The parameter is already typed, so `typeof`
  // narrows it properly and no shape is fabricated: the question is only
  // whether the value that actually arrived is a row at all.
  return typeof status === 'object' && status !== null && typeof status.state === 'string'
    ? status
    : null;
}

export function publishAvailability(
  draft: DraftRow,
  status: PublishJobStatusRow | null | undefined,
): PublishAvailability {
  const job = asJobStatus(status);
  const jobFailed = job !== null && job.state === 'failed';
  // A FAILED JOB IS NOT AUTOMATICALLY A RETRYABLE ONE, and this branch used to
  // say it was — which made the panel promise something the database refuses.
  //
  // `app.publish_draft` accepts a draft in exactly two states, `approved` and
  // `publish_failed` (`0033:1340` refuses anything else with
  // `acl: draft_state_conflict`). The two rows are written by different code: the
  // worker marks `publish_jobs.state` failed with its own `last_error`, and
  // `app.mark_publish_failed` (0058) is what moves the draft, called from inside
  // `SQL.failJob`'s own transaction — so the two rows cannot disagree. It is
  // conditioned on the job's committed `state = 'failed'`, NOT on "a refusal
  // happened": a retryable failure requeues the job as `queued` and must leave
  // the draft in `publishing`, because that is the state holding
  // `drafts_one_active_publication_idx`.
  //
  // An earlier version of this comment said nothing in the repository moved a
  // draft to `publish_failed`, and was right when written. 0058 made it false,
  // and the `publishable` line below now depends on the opposite being true.
  // Measured on a private replay of 0001..0070, running the worker's own
  // `SQL.failJob` then `SQL.markPublishFailed`: job `running` -> `failed`, one
  // draft moved, `version` 7 -> 8, and `app.publish_job_status` handed the
  // signed-in browser `state=failed, draft_state=publish_failed`.
  //
  // A FAILED JOB OVER A DRAFT THAT IS NOT PUBLISHABLE is the failure mode this
  // whole panel exists to avoid, and it is the one my own first cut produced:
  // `publicationFailed()` sees the job, the control renders ENABLED, the
  // sentence says "Publishing again re-runs the same approved content as a new
  // attempt" — and every press is a 409. Worse,
  // `drafts_one_active_publication_idx` is unique per skill on
  // `state = 'publishing'`, so a draft left there holds the skill's only
  // publication slot and no other draft of that skill can be published either.
  // 0058 makes that state unreachable through the worker's own failure path;
  // the branch is kept because a draft can be moved by something else, and a
  // panel that promises a retry the database will refuse is the defect.
  //
  // So retry is offered only where a retry can actually be requested, and the
  // state the panel cannot offer one in is named rather than papered over.
  const publishable = draft.state === 'approved' || draft.state === 'publish_failed';
  if (jobFailed || draft.state === 'publish_failed') {
    if (publishable) {
      return {
        allowed: true,
        reason:
          'This publication failed and nothing was released. Publishing again re-runs the same ' +
          'approved content as a new attempt.',
      };
    }
    return {
      allowed: false,
      reason:
        'This publication failed, but the draft was left in a state a publication cannot be ' +
        'requested from, so a retry would be refused and nothing would be released. This is a ' +
        'fault in the publication pipeline — not something pressing a button clears.',
    };
  }
  if (draft.state === 'publishing') {
    return {
      allowed: false,
      reason:
        'This draft is being published right now. The previous release stays live until the new ' +
        'one is verified, so nothing is unavailable while this runs.',
    };
  }
  if (draft.state === 'published') {
    return {
      allowed: false,
      reason: 'This draft has already been published. Its content is the current release.',
    };
  }
  if (draft.state === 'in_review') {
    return {
      allowed: false,
      reason: 'This draft is with a reviewer. Publication needs a decision on this exact content first.',
    };
  }
  if (draft.state === 'draft') {
    return {
      allowed: false,
      reason: 'This draft has not been submitted for review yet, so there is nothing approved to publish.',
    };
  }
  if (draft.state === 'approved') {
    return {
      allowed: true,
      reason: 'Publishing creates a release and makes it the current one for this skill.',
    };
  }
  // A job that reached a terminal state other than `failed`, on a draft whose
  // own state did not move with it. Reachable only for `cancelled` and for a
  // `succeeded` job read against a stale draft row, and it is here so that no
  // terminal job can be read as "ready to publish" by falling through.
  if (job !== null && isTerminal(job.state)) {
    return {
      allowed: false,
      reason:
        job.state === 'cancelled'
          ? 'This publication was cancelled. Nothing was released, and the previous release is still current.'
          : 'This publication has finished. The outcome is below.',
    };
  }
  return {
    allowed: false,
    reason: `This draft is ${draft.state.replace('_', ' ')}, which cannot be published.`,
  };
}

/** One line describing a job, for the live region and the status list. */
export function describePublication(status: PublishJobStatusRow): string {
  switch (status.state) {
    case 'queued':
      return 'Queued for publication. The previous release is still current.';
    case 'running':
      return `Publishing. Attempt ${status.attempts + 1}. The previous release is still current.`;
    case 'succeeded':
      return `Published. The skill's current release is ${status.current_revision ?? 'a new release'}.`;
    case 'failed':
      return `Publication failed. ${status.last_error ?? 'No reason was recorded.'}`;
    case 'cancelled':
      return 'This publication was cancelled. The previous release is still current.';
    default:
      return `Publication state: ${status.state}.`;
  }
}

/** The tag for a job. Glyph and word, never colour alone. */
function statusTag(status: PublishJobStatusRow): RawHtml {
  switch (status.state) {
    case 'queued':
      return tag('Queued', 'Q', 'accent');
    case 'running':
      return tag('Publishing', '…', 'accent');
    case 'succeeded':
      return tag('Published', '✓', 'accent');
    case 'failed':
      return tag('Publication failed', '!', 'danger');
    case 'cancelled':
      return tag('Cancelled', '–', 'attention');
    default:
      return tag(status.state, '?', 'plain');
  }
}

export interface PublicationPanelInput {
  readonly orgSlug: string;
  readonly draft: DraftRow;
  readonly status: PublishJobStatusRow | null | undefined;
  /** True while the page has a request in flight for this draft. */
  readonly busy: boolean;
  /**
   * Why the publication could not be read, when a read was attempted and failed.
   *
   * A separate field from `status`, because the two cannot be collapsed into
   * `status === null`. `null` says "this draft has no publication" and that is
   * a fact; a failed read says "this draft's publication is unknown right now",
   * and rendering the second as the first would be the false-negative twin of
   * the false success this panel exists to avoid — a person would be told
   * nothing is publishing when in fact nobody can see whether something is.
   */
  readonly readFailure: Failure | null;
}

export function renderPublication(input: PublicationPanelInput): RawHtml {
  const { draft } = input;
  // Same reasoning as `asJobStatus`, for the same reason: an omitted key is
  // `undefined`, not `null`, and `readFailure !== null` would then be true and
  // `presentFailure(undefined)` would throw instead of rendering.
  const readFailure =
    typeof input.readFailure === 'object' && input.readFailure !== null ? input.readFailure : null;
  const status = asJobStatus(input.status);
  const availability = publishAvailability(draft, status);
  const inFlight = status !== null && !isTerminal(status.state);
  // Both rows, for the reason on `publicationFailed`: the panel must not keep
  // saying "publishing" over a job the worker has already given up on.
  const failed = publicationFailed(draft, status);
  // A control that creates a permanent release revision is refused by the
  // database whenever anything above is not known to be safe, so the panel
  // offers it on the same terms the server will: nothing in flight, no request
  // of our own outstanding, and no unreadable state to reason about.
  const canPress = availability.allowed && !inFlight && !input.busy && readFailure === null;
  // The worker's reason, when there is a job row to read it from. `failed` can
  // be true because the DRAFT row moved while the job row has not — the two are
  // written by different code — so this cannot be reached through `status`
  // alone.
  const reason = status?.last_error ?? null;

  // WHAT THIS BRANCH MAY CLAIM. Every failure that reaches here is one where
  // the panel genuinely does not know, and `presentFailure`'s `not-found`
  // sentence — "this record does not exist, or you are not permitted to see
  // it" — is the honest one for a 404 that arrives against a draft whose state
  // admits a job (`getPublication`'s `NEVER_PUBLISHED_STATES` decides that, and
  // the never-published case is folded to `null` upstream rather than reaching
  // this line). So this branch is reached for a publication that is UNKNOWN, and
  // never for one that does not exist: saying "not found" about a draft nobody
  // has published yet would be a false statement about a real record, and it is
  // the reason the fold lives in the query layer rather than here.
  if (readFailure !== null) {
    const presented = presentFailure(readFailure);
    return html`
      <div class="panel" id="publication" aria-busy="${input.busy ? 'true' : 'false'}">
        <div class="panel-head">
          <h2 id="publication-heading">Publication</h2>
          ${tag('Unknown', '?', 'attention')}
        </div>
        <div class="panel-body stack-sm">
          <div class="callout callout-attention" role="alert">
            <span class="callout-tag">Cannot be read</span>
            <p><strong>${presented.title}.</strong> ${presented.body}</p>
          </div>
          ${status === null
            ? ''
            : html`
                <p class="state-meta">
                  The last state this screen could read was
                  <strong>${status.state}</strong>. That is what was read, not what is true now.
                </p>
              `}
          <div class="btn-row">
            <button class="btn btn-primary" data-action="review:reload">Re-read</button>
          </div>
        </div>
      </div>
    `;
  }
  return html`
    <div class="panel" id="publication" aria-busy="${input.busy || inFlight ? 'true' : 'false'}">
      <div class="panel-head">
        <h2 id="publication-heading">Publication</h2>
        ${status === null ? tag('Not published', '·', 'plain') : statusTag(status)}
      </div>
      <div class="panel-body stack-sm">
        <p class="cell-note" id="publication-availability">${availability.reason}</p>

        <!--
          THE LIVE REGION. Always present, always polite except in the failure
          case, so that assistive technology has the node to watch BEFORE the
          text changes into it. A region created at the same moment as its
          content is announced unreliably, which is why this div is not
          conditional on there being something to say.
        -->
        <div
          class="${failed ? 'callout callout-danger' : 'visually-hidden'}"
          role="${failed ? 'alert' : 'status'}"
          aria-live="${failed ? 'assertive' : 'polite'}"
        >
          ${status === null ? '' : html`<p>${describePublication(status)}</p>`}
          ${failed && reason !== null
            ? html`<p class="state-meta"><strong>Reason:</strong> ${reason}</p>`
            : failed
              ? html`<p class="state-meta">No reason was recorded for this failure.</p>`
              : ''}
        </div>

        ${status === null
          ? ''
          : html`
              <dl class="dl">
                <dt>Job</dt>
                <dd class="cell-meta">${status.id}</dd>
                <dt>State</dt>
                <dd>${status.state}</dd>
                <dt>Attempts</dt>
                <dd>${status.attempts}</dd>
                ${status.created_at === null ? '' : html`<dt>Requested</dt><dd>${formatDateTime(status.created_at)}</dd>`}
                ${status.completed_at === null
                  ? ''
                  : html`<dt>Finished</dt><dd>${formatDateTime(status.completed_at)}</dd>`}
                <dt>Draft state</dt>
                <dd>${status.draft_state}</dd>
                ${status.current_release_id === null
                  ? ''
                  : html`
                      <dt>Current release</dt>
                      <dd>
                        ${status.current_revision === null ? '' : html`<strong>${status.current_revision}</strong> `}
                        <a href="/o/${input.orgSlug}/skills/${status.skill_id}">${status.current_release_id}</a>
                      </dd>
                    `}
              </dl>
            `}

        <div class="btn-row">
          ${failed
            ? html`
                <button
                  class="btn btn-primary"
                  data-action="publication:retry"
                  aria-describedby="publication-availability"
                  ${availability.allowed && !input.busy ? '' : 'disabled'}
                >
                  ${input.busy ? 'Publishing…' : 'Publish again'}
                </button>
              `
            : html`
                <button
                  class="btn btn-primary"
                  data-action="publication:publish"
                  aria-describedby="publication-availability"
                  ${canPress ? '' : 'disabled'}
                >
                  ${inFlight ? 'Publishing…' : 'Publish'}
                </button>
              `}
          <button class="btn btn-secondary" data-action="review:reload">Re-read</button>
        </div>

        ${inFlight
          ? html`
              <p class="state-meta">
                This panel keeps checking for you while the publication runs. It stops on its own when
                the publication succeeds or fails; nothing here refreshes the page.
              </p>
            `
          : ''}
        ${status !== null && status.state === 'succeeded'
          ? html`
              <p class="state-meta">
                The release id above is the skill's CURRENT release. A later publication can move
                it, so it is not a claim that this job produced it.
              </p>
            `
          : ''}
      </div>
    </div>
  `;
}

/* -------------------------------------------------------------------------
 * The poll
 * ---------------------------------------------------------------------- */

/**
 * WHICH ENDPOINT THIS POLLS, AND WHY THAT ONE.
 *
 * `GET /v1/drafts/:draftId/publication` — draft-scoped, NOT `/v1/publish-jobs/
 * :jobId`. Three reasons, and the third is the one that decides it:
 *
 *   1. The job id is not something a person has. `POST .../publish` answers 202
      with a job, but the next thing that happens is usually someone closing the
      tab. On return the browser holds a draft id from the url and no job id at
      all, so a job-scoped poll has nothing to poll.
 *   2. `status_url` on the 202 is already draft-scoped, and so is the `Location`
      header. A job-scoped poller would be reading an endpoint the API does not
      have; there is deliberately no second way to ask the same question
 *      (`apps/api/src/routes/publication.ts:271-281`).
 *   3. `last_error` — the one field that separates "the worker gave up" from
 *      "the worker is still going" — lives on the job row. The draft-scoped
 *      function returns the LATEST job for that draft, so a failed publication
 *      found after a reload still carries its reason. A screen that only knew
 *      the draft id would otherwise have to guess, and the guess is always
 *      "still publishing" (0048's own header says this).
 *
 * WHY THE TIMER IS CHAINED AND NOT AN INTERVAL. One read is issued, and the
 * next is scheduled only once that read has come back. An interval would keep
 * firing on a slow API and stack up concurrent reads for the same draft, and
 * the last one to land would win regardless of order.
 */

/*
 * The outstanding read, as a cancellation rather than a timer handle. A handle
 * would have to be typed with the platform's own timer type, and every caller
 * that could reach it would have to clear a number it never interprets.
 */
let cancelPoll: (() => void) | null = null;
let pollingDraftId: string | null = null;

interface PublicationScreenData {
  readonly draft: { readonly id: string; readonly state: string };
  readonly publication: PublishJobStatusRow | null;
  readonly publicationFailure: Failure | null;
}


/**
 * The screen's data, if it is still this draft's review screen.
 *
 * A timer outlives the screen that started it, and `paint()` replaces the DOM
 * on every state change, so the poll cannot hold a node or a closure over one.
 * It re-derives what it is allowed to touch from `app.state` on every tick, and
 * a tick that lands after a navigation finds a different screen and does
 * nothing.
 */
function screenDataFor(app: AppShell, draftId: string): PublicationScreenData | null {
  const route = app.state.route;
  if (route.name !== 'review' || route.draftId !== draftId) return null;
  const screen = app.state.screen;
  if (screen.kind !== 'ready' || typeof screen.data !== 'object' || screen.data === null) return null;
  const data = screen.data as Partial<PublicationScreenData>;
  return data.draft?.id === draftId ? (data as PublicationScreenData) : null;
}

function applyPublication(
  app: AppShell,
  draftId: string,
  change: Partial<PublicationScreenData>,
): void {
  const data = screenDataFor(app, draftId);
  if (data === null) return;
  app.setScreen({ kind: 'ready', data: { ...data, ...change } });
}

function schedule(app: AppShell, draftId: string): void {
  const timer = setTimeout(() => {
    cancelPoll = null;
    pollingDraftId = null;
    void tick(app, draftId);
  }, PUBLISH_POLL_MS);
  cancelPoll = () => {
    clearTimeout(timer);
  };
  pollingDraftId = draftId;
}

async function tick(app: AppShell, draftId: string): Promise<void> {
  const before = screenDataFor(app, draftId);
  if (before === null) return;

  const read = await api.getPublication(app.client, draftId, before.draft.state);

  // Checked again AFTER the await, because navigation during the read is the
  // common case and writing this publication onto some other screen's data
  // would put a release id in front of somebody who never asked for it.
  if (screenDataFor(app, draftId) === null) return;

  if (!read.ok) {
    /*
     * A 404 HERE means something other than "never published". The screen read
     * this draft successfully a moment ago, so the caller may read it; what
     * `publish_job_status` withholds is the JOB, and it returns the same zero
     * rows for a job the caller may no longer manage as for one that does not
     * exist. During a publication the second reading is the one that matters —
     * permission can be revoked while a job waits (PUB-04) — so the panel is told
     * the status is UNKNOWN and the poll stops. Rendering it as "not published"
     * would be a false statement in the safe direction, which is how a false
     * statement gets shipped.
     *
     * This is why the poll passes the draft's state and relies on the fold
     * rather than re-deciding the question here: the draft being polled is in
     * `publishing`, which is NOT one of the states that fold a 404, so the
     * reasoning above reaches this branch as a failure and the two paths cannot
     * disagree about the same response.
     */
    applyPublication(app, draftId, { publicationFailure: read.failure });
    return;
  }

  // `read.value` is null only when the draft's own state proved no job can
  // exist, which a draft being polled cannot be — but it is a `null` the type
  // permits, and `isTerminal(read.value.state)` on it would throw. A poll that
  // is told "no publication" has nothing left to follow.
  if (read.value === null) {
    applyPublication(app, draftId, { publication: null, publicationFailure: null });
    return;
  }

  applyPublication(app, draftId, { publication: read.value, publicationFailure: null });
  // One more thing has to happen after a SUCCEEDED job: the draft row and the
  // skill's release both moved, and only a reload re-reads them. Publishing a
  // release is the one transition on this screen that changes rows the panel
  // itself does not hold, so leaving them stale would show "approved" against
  // a skill that is already released.
  if (isTerminal(read.value.state)) {
    void app.reload();
    return;
  }
  schedule(app, draftId);
}

/**
 * Begin following a publication, if the one on screen is still running.
 *
 * Called after every review load rather than only after a Publish press, which
 * is what makes a reloaded tab resume following a job that started while
 * nobody was looking — the case a job-scoped poll could not even express.
 */
export function startPublicationPoll(app: AppShell, draftId: string): void {
  const data = screenDataFor(app, draftId);
  if (data === null) return;
  const job = asJobStatus(data.publication);
  if (job === null || isTerminal(job.state)) return;
  if (pollingDraftId === draftId) return;
  stopPublicationPoll();
  schedule(app, draftId);
}

/** Stop following. A terminal state, a navigation, or a failure all end it. */
export function stopPublicationPoll(): void {
  if (cancelPoll !== null) cancelPoll();
  cancelPoll = null;
  pollingDraftId = null;
}

